import type { AdminPrincipalPayload, AdminSchema, ApiResponse } from '@wanjianshi/shared-contracts';
import { ApiError } from '@wanjianshi/shared-http';
import { create } from 'zustand';
import { adminApi, configureAdminHttpHandlers } from '@/services/api/client';

type AuthStatus = 'booting' | 'authenticated' | 'anonymous';

interface AuthState {
  status: AuthStatus;
  principal: AdminPrincipalPayload | null;
  forbiddenMessage: string | null;
  bootstrap: () => Promise<void>;
  login: (account: string, password: string, captchaToken?: string) => Promise<void>;
  logout: () => Promise<void>;
  updatePrincipal: (principal: AdminPrincipalPayload) => void;
  hasPermission: (permission: string) => boolean;
  clearForbidden: () => void;
}

function unwrap<T>(response: ApiResponse<T>): T {
  return response.data;
}

let bootstrapPromise: Promise<void> | null = null;
let refreshPromise: Promise<boolean> | null = null;

function refreshAdminSession(
  set: (state: Partial<AuthState>) => void,
): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = adminApi.post<ApiResponse<AdminPrincipalPayload>>(
      '/admin/auth/refresh',
      undefined,
      { skipUnauthorizedHandler: true },
    ).then(response => {
      set({
        status: 'authenticated',
        principal: unwrap(response),
        forbiddenMessage: null,
      });
      return true;
    }).catch(() => {
      set({ status: 'anonymous', principal: null });
      return false;
    }).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function restoreAdminSession(
  set: (state: Partial<AuthState>) => void,
): Promise<void> {
  try {
    const me = await adminApi.get<ApiResponse<AdminPrincipalPayload>>('/admin/me', {
      skipUnauthorizedHandler: true,
    });
    set({ status: 'authenticated', principal: unwrap(me) });
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      if (await refreshAdminSession(set)) return;
    }
    set({ status: 'anonymous', principal: null });
  }
}

export const useAdminAuth = create<AuthState>((set, get) => ({
  status: 'booting',
  principal: null,
  forbiddenMessage: null,
  async bootstrap() {
    if (!bootstrapPromise) {
      bootstrapPromise = restoreAdminSession(set).finally(() => {
        bootstrapPromise = null;
      });
    }
    await bootstrapPromise;
  },
  async login(account, password, captchaToken) {
    const payload: AdminSchema<'AdminLoginRequest'> = {
      account,
      password,
      captcha_token: captchaToken ?? null,
    };
    const response = await adminApi.post<ApiResponse<AdminPrincipalPayload>>(
      '/admin/auth/login',
      payload,
      { skipUnauthorizedHandler: true },
    );
    set({ status: 'authenticated', principal: unwrap(response), forbiddenMessage: null });
  },
  async logout() {
    try {
      await adminApi.post('/admin/auth/logout', undefined, {
        skipUnauthorizedHandler: true,
      });
    } catch (error) {
      if (!(error instanceof ApiError && error.status === 401)) throw error;
    } finally {
      set({ status: 'anonymous', principal: null });
    }
  },
  updatePrincipal(principal) {
    set({ status: 'authenticated', principal });
  },
  hasPermission(permission) {
    const permissions = get().principal?.permissions ?? [];
    return permissions.includes('wjs-admin:*')
      || permissions.includes(permission)
      || permissions.some(granted => granted.endsWith(':*') && permission.startsWith(granted.slice(0, -1)));
  },
  clearForbidden() {
    set({ forbiddenMessage: null });
  },
}));

configureAdminHttpHandlers({
  recoverUnauthorized: () => refreshAdminSession(useAdminAuth.setState),
  onUnauthorized: () => useAdminAuth.setState({ status: 'anonymous', principal: null }),
  onForbidden: message => useAdminAuth.setState({ forbiddenMessage: message }),
});
